export type PlatformCallSafety = 'read' | 'write' | 'destructive';

export interface PlatformCallFrameParams {
  resource: string;
  method: string;
  arguments: Record<string, unknown>;
  safety: PlatformCallSafety;
  idempotency_key: string;
  /**
   * Server-stamped destructive pre-authorization.
   * Set true ONLY by the Go tools worker when a TRUSTED
   * DirectInvocationOverride (from the executeTool request, never model
   * `arguments`) confirmed the destructive action, so the direct/agent-less path
   * completes without a chat-thread confirmation. Absent/false on the agent path
   * (the confirmation gate stands). The client trusts this exactly like
   * `safety`: it is authoritative and cannot be set from model arguments.
   */
  pre_authorized?: boolean;
}

export type PlatformCallResult =
  | { status: 'ok'; result: unknown }
  | { status: 'error'; error: string }
  | {
      status: 'confirmation_required';
      preview: { resource: string; method: string; arguments: Record<string, unknown> };
    };

export type PlatformCallExecutorFn = (
  call: PlatformCallFrameParams,
) => Promise<PlatformCallResult>;

export interface McpInvokeFrame {
  id: string | number;
  method: 'mcp.invoke';
  params: {
    mcpId: string;
    toolId: string;
    params: PlatformCallFrameParams;
  };
}

export interface McpInvokeResponseFrame {
  jsonrpc: '2.0';
  id: string | number;
  result: PlatformCallResult;
}

let registeredExecutor: PlatformCallExecutorFn | null = null;

export function registerPlatformCallExecutor(fn: PlatformCallExecutorFn): void {
  registeredExecutor = fn;
}

export function unregisterPlatformCallExecutor(): void {
  registeredExecutor = null;
}

export async function handlePlatformCallFrame(
  frame: McpInvokeFrame,
): Promise<McpInvokeResponseFrame | null> {
  if (frame.params?.toolId !== 'platform_call') {
    return null;
  }

  if (!registeredExecutor) {
    return {
      jsonrpc: '2.0',
      id: frame.id,
      result: {
        status: 'error',
        error: 'platform_call executor is not registered on this client',
      },
    };
  }

  try {
    const result = await registeredExecutor(frame.params.params);
    return { jsonrpc: '2.0', id: frame.id, result };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      jsonrpc: '2.0',
      id: frame.id,
      result: { status: 'error', error: message },
    };
  }
}
